import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Checkbox, Col, Divider, Input, InputRef, Modal, Row } from 'antd';
import { capitalCase } from 'change-case';
import { TbArrowRight, TbDatabase, TbMist, TbTextPlus, TbX } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { ResourceDraft, useDirtyResourceDraft } from '@/hooks/useResourceDrafts';
import { useSubmitShortcut } from '@/hooks/useSubmitShortcut';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import {
    DataSourcesDropdown,
    EmptySection,
    LearnMoreFooter,
    VirtualizedList as List,
    ListTypeSelectionDropdown,
} from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useListStore } from '@/stores';
import { imgLogoStyling } from '@/theme';
import {
    ConnectedAccount,
    ConnectedAccountType,
    ListType,
    ListVisibility,
    List as RelationsList,
    SetStateMethod,
} from '@/types';
import { formatDate } from '@/utilities';

import CreateMoreControl from '../components/CreateMoreControl';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';
import { MAX_TITLE_LENGTH } from '../CreateDashboardModal';
import AutopopulatedColumnsDropdown from './AutopopulatedColumnsDropdown';

interface CreateListModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    initialDraft?: ResourceDraft<any> | null;
    onDraftResolved?: () => void;
}

const CreateListModal: FC<CreateListModalProps> = ({ open, setOpen, initialDraft, onDraftResolved }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    // Autofocus
    const titleInputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => titleInputRef.current?.focus(), 125);
        }
    }, [open]);

    const { token } = useAntDesignTheme();

    const [visibility, setVisibility] = useState('Private');

    const [icon, setIcon] = useState(null);
    const [iconBackgroundColor, setIconBackgroundColor] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');

    const [listType, setListType] = useState<ListType>(ListType.PEOPLE);
    const [listView, setListView] = useState('Table');

    const [view, setView] = useState('options');

    const [selectedDataSources, setSelectedDataSources] = useState<ConnectedAccount[]>([]);

    const [autopopulateColumns, setAutopopulateColumns] = useState(true);

    const { createList, lists } = useListStore();
    const createAccess = useWorkspaceResourceCreateAccess('lists', lists?.length ?? 0);

    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [createMoreEnabled, setCreateMoreEnabled] = useState<boolean>(false);
    const normalizedTitle = useMemo(() => title.trim(), [title]);

    const resetForm = () => {
        setIcon(null);
        setIconBackgroundColor(null);
        setTitle('');
        setDescription('');
        setVisibility('Private');
        setListType(ListType.PEOPLE);
        setListView('Table');
        setView('options');
        setSelectedDataSources([]);
        setAutopopulateColumns(true);
    };

    const { dirtyDraftModal, markSubmitted, requestClose } = useDirtyResourceDraft({
        close: () => setOpen(false),
        draftId: initialDraft?.id,
        isDirty:
            title.trim() !== '' ||
            description.trim() !== '' ||
            visibility !== 'Private' ||
            listType !== ListType.PEOPLE ||
            listView !== 'Table' ||
            selectedDataSources.length !== 0 ||
            !autopopulateColumns,
        reset: resetForm,
        resourceLabel: 'List',
        resourceType: 'list',
        title,
        values: {
            autopopulateColumns,
            description,
            icon,
            iconBackgroundColor,
            listType,
            listView,
            selectedDataSources,
            title,
            visibility,
        },
    });

    const titleError = useMemo(() => {
        if (!normalizedTitle) {
            return 'Dashboard Name is Required';
        }

        if (normalizedTitle.length > MAX_TITLE_LENGTH) {
            return `Must be ${MAX_TITLE_LENGTH} characters or fewer`;
        }
        return null;
    }, [normalizedTitle]);

    const canSubmit = !submitting && !titleError;

    const handleCreateList = async () => {
        if (!canSubmit || createAccess.disabled) {
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                icon: icon,
                iconBackgroundColor: iconBackgroundColor,
                name: title,
                description: description,
                archived: false,
                favorited: false,
                locked: false,
                type: listType,
                columns: [],
                stages: [],
                autopopulateColumns: autopopulateColumns,
                dataSources: selectedDataSources,
                dataSourceFilters: [],
                publiclyVisible: false,
                visibility: visibility as ListVisibility,
                createdAt: new Date(),
                updatedAt: new Date(),
                views: [],
            } as Partial<RelationsList | any>;

            const l = await createList(payload);

            markSubmitted();
            onDraftResolved?.();
            resetForm();

            toast('List Created', {
                icon: <TbMist size='1rem' />,
                description: l?.name,
            });

            if (!createMoreEnabled) {
                setOpen(false);
                navigate(`/lists/${l?.id}`);
            } else {
                titleInputRef.current?.focus();
            }
        } catch (error) {
            captureException(error);
        } finally {
            setSubmitting(false);
        }
    };

    const { listTypeToIconMap } = useMaps();

    useSubmitShortcut(open && canSubmit && !createAccess.disabled, handleCreateList, [
        createAccess.disabled,
        normalizedTitle,
        description,
        listType,
        visibility,
        autopopulateColumns,
        selectedDataSources,
        createMoreEnabled,
    ]);

    useEffect(() => {
        if (open && initialDraft?.values) {
            setIcon(initialDraft.values.icon ?? null);
            setIconBackgroundColor(initialDraft.values.iconBackgroundColor ?? null);
            setTitle(initialDraft.values.title ?? '');
            setDescription(initialDraft.values.description ?? '');
            setVisibility(initialDraft.values.visibility ?? 'Private');
            setListType(initialDraft.values.listType ?? ListType.PEOPLE);
            setListView(initialDraft.values.listView ?? 'Table');
            setSelectedDataSources(initialDraft.values.selectedDataSources ?? []);
            setAutopopulateColumns(initialDraft.values.autopopulateColumns ?? true);
        }
    }, [open, initialDraft]);

    const onSelectDataSourceHandler = (connectedAccount: ConnectedAccount) => {
        setSelectedDataSources([...selectedDataSources, connectedAccount]);
    };

    const { integrationLabelToIconMap, listTypeToAutopopulatingColumnsMapMap } = useMaps();

    const autopopulatedColumnCount = useMemo(() => {
        const map = listTypeToAutopopulatingColumnsMapMap[listType];

        if (!map || selectedDataSources.length === 0) {
            return 0;
        }

        const allColumns = selectedDataSources.flatMap((ds) => {
            return map[ds?.type as ConnectedAccountType] || [];
        });

        // de-dupe by column key
        const uniqueByKey = new Map<string, any>();
        allColumns.forEach((col) => {
            if (col.key) {
                uniqueByKey.set(col.key, col);
            }
        });

        return uniqueByKey.size;
    }, [listType, selectedDataSources, listTypeToAutopopulatingColumnsMapMap]);

    return (
        <>
            <Modal
                open={open}
                title={
                    <ResourceCreationModalTitle
                        icon={<TbTextPlus size='1rem' style={{ verticalAlign: '-0.175rem' }} />}
                        title='Create List'
                        visibility={visibility}
                        setVisibility={setVisibility}
                    />
                }
                mask={false}
                style={{ minWidth: '50rem' }}
                styles={{ body: { padding: 0 } }}
                destroyOnHidden
                onCancel={requestClose}
                footer={
                    <Row align={'middle'}>
                        <Col flex='none'>
                            <LearnMoreFooter align='left' topic='Lists' />
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none'>
                            <CreateMoreControl
                                checked={createMoreEnabled}
                                disabled={createAccess.disabled}
                                onChange={setCreateMoreEnabled}
                            />
                        </Col>
                        <Col flex='none'>
                            <Button
                                type='primary'
                                onClick={handleCreateList}
                                disabled={loading || title === '' || !canSubmit || createAccess.disabled}
                                loading={loading || submitting}
                            >
                                Create List
                                <TbArrowRight size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <div
                    style={{
                        borderTop: `1px solid ${token.colorBorder}`,
                        borderBottom: `1px solid ${token.colorBorder}`,
                        marginTop: '1rem',
                        paddingTop: '0.5rem',
                        marginBottom: '0.5rem',
                    }}
                >
                    <Form layout='vertical'>
                        <Row gutter={[16, 0]}>
                            <Col flex='auto'>
                                <Form.Item style={{ marginBottom: '0.5rem' }} label='Name'>
                                    <Input
                                        ref={titleInputRef}
                                        addonBefore={
                                            <div style={{ margin: '0 -0.25rem' }}>
                                                <TbMist
                                                    size='1rem'
                                                    style={{
                                                        verticalAlign: '-0.175rem',
                                                        color: token.colorTextQuaternary,
                                                    }}
                                                />
                                            </div>
                                        }
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder='List Name...'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item style={{ marginBottom: '0.5rem' }} label='List Type'>
                                    <ListTypeSelectionDropdown listType={listType} setListType={setListType}>
                                        <DropdownButton block>
                                            <Row style={{ width: '100%' }} wrap={false}>
                                                <Col flex='none'>{listTypeToIconMap[listType]}</Col>
                                                <Col flex='none'>{listType && capitalCase(listType)}</Col>
                                                <Col flex='auto' />
                                            </Row>
                                        </DropdownButton>
                                    </ListTypeSelectionDropdown>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col flex='auto'>
                                <Form.Item style={{ marginBottom: '0.5rem' }} label='Description'>
                                    <Input.TextArea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder='List Description...'
                                        rows={3}
                                        style={{ resize: 'none' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider style={{ margin: '0.5rem 0' }} />
                        <div>
                            <small style={{ color: token.colorTextTertiary }}>Data Sources</small>
                        </div>
                        <div style={{ marginTop: '0.5rem' }}>
                            <DataSourcesDropdown width={'auto'} onSelectDataSourceHandler={onSelectDataSourceHandler}>
                                <DropdownButton block>
                                    <Row wrap={false} style={{ width: '100%' }} align={'middle'} gutter={[6, 0]}>
                                        <Col flex='none' style={{ paddingLeft: 0 }}>
                                            <TbDatabase size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                                        </Col>
                                        <Col flex='none'>Select Data Sources...</Col>
                                        <Col flex='auto' />
                                    </Row>
                                </DropdownButton>
                            </DataSourcesDropdown>
                        </div>
                        {selectedDataSources?.length !== 0 ? (
                            <List
                                style={{
                                    minHeight: '20rem',
                                    maxHeight: '20rem',
                                }}
                                dataSource={selectedDataSources}
                                renderItem={(ds: ConnectedAccount) => {
                                    return (
                                        <List.Item key={ds?.id}>
                                            <List.Item.Meta
                                                avatar={
                                                    <div style={{ marginTop: '0.2rem' }}>
                                                        <Avatar
                                                            size={'large'}
                                                            shape='square'
                                                            icon={
                                                                <img
                                                                    alt={ds?.type}
                                                                    src={
                                                                        integrationLabelToIconMap[
                                                                            ds?.type as ConnectedAccountType
                                                                        ]
                                                                    }
                                                                    style={{
                                                                        ...imgLogoStyling,
                                                                        transform: 'scale(0.75)',
                                                                    }}
                                                                />
                                                            }
                                                            style={{ border: `1px solid ${token.colorBorder}` }}
                                                        />
                                                    </div>
                                                }
                                                title={ds?.nickname ?? ds?.type}
                                                description={
                                                    ds?.updatedAt && <>Last synced {formatDate(ds?.updatedAt)}</>
                                                }
                                            />
                                            <Button
                                                type='dashed'
                                                onClick={() => {
                                                    const filtered = selectedDataSources?.filter(
                                                        (fds) => fds?.id !== ds?.id,
                                                    );

                                                    setSelectedDataSources(filtered);
                                                }}
                                            >
                                                <TbX style={{ color: token.colorTextTertiary }} />
                                            </Button>
                                        </List.Item>
                                    );
                                }}
                            />
                        ) : (
                            <EmptySection
                                height='20rem'
                                icon={<TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />}
                                label='No Data Sources Added Yet'
                                description='Lists are best used with Data Sources. We recommend starting with an Email and Calendar Data Source to aggregate your investment research and institutional relationships in one place.'
                            />
                        )}
                        <Divider style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />
                        <Row wrap={false} align={'top'} gutter={[8, 0]}>
                            <Col flex={'auto'}>
                                <Form.Item label='Additional Settings' style={{ marginBottom: '0.75rem' }}>
                                    <Row align={'top'} gutter={[16, 0]} wrap={false}>
                                        <Col flex='auto'>
                                            <div>
                                                <Checkbox
                                                    checked={autopopulateColumns}
                                                    onClick={(e) => {
                                                        setAutopopulateColumns((b) => !b);
                                                    }}
                                                    style={{ marginRight: '0.5rem' }}
                                                />
                                                <svg
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    width='1rem'
                                                    height='1rem'
                                                    style={{
                                                        verticalAlign: '-0.175rem',
                                                        transform: 'rotate(180deg)',
                                                        marginRight: '0.375rem',
                                                    }}
                                                    viewBox='0 0 24 24'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    strokeWidth='2'
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    className='icon icon-tabler icons-tabler-outline icon-tabler-chart-column'
                                                >
                                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                                    <path d='M4 20h3' />
                                                    <path d='M17 20h3' />
                                                    <path d='M10.5 20h3' />
                                                    <path d='M4 16h3' />
                                                    <path d='M17 16h3' />
                                                    <path d='M10.5 16h3' />
                                                    <path d='M4 12h3' />
                                                    <path d='M17 12h3' />
                                                    <path d='M10.5 12h3' />
                                                    <path d='M4 8h3' />
                                                    <path d='M17 8h3' />
                                                    <path d='M4 4h3' />
                                                </svg>
                                                Autopopulate Columns
                                            </div>
                                            <div>
                                                <small style={{ color: token.colorTextTertiary }}>
                                                    Populates your List with default columns based on your List Type &
                                                    Data Sources. Columns can always be changed at any point in the
                                                    future.
                                                </small>
                                            </div>
                                        </Col>
                                        <Col flex='none'>
                                            <AutopopulatedColumnsDropdown
                                                dataSources={selectedDataSources}
                                                listType={listType}
                                            >
                                                <Button size='small' type='dashed' disabled={!autopopulateColumns}>
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        width='0.875rem'
                                                        height='0.875rem'
                                                        viewBox='0 0 24 24'
                                                        stroke={token.purple5}
                                                        fill={token.purple2}
                                                        // fill='none'
                                                        // stroke='currentColor'
                                                        strokeWidth='2'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        className='icon icon-tabler icons-tabler-outline icon-tabler-cube-spark'
                                                    >
                                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                                        <path d='M21 12v-4.01a1.98 1.98 0 0 0 -1 -1.717l-7 -4.008a2.02 2.02 0 0 0 -2 0l-7 4.008c-.619 .355 -1 1.01 -1 1.718v8.018c0 .709 .381 1.363 1 1.717l7 4.008c.62 .354 1.38 .354 2 0' />
                                                        <path d='M12 22v-10' />
                                                        <path d='M12 12l8.73 -5.04' />
                                                        <path d='M3.27 6.96l8.73 5.04' />
                                                        <path d='M19 22.5a4.75 4.75 0 0 1 3.5 -3.5a4.75 4.75 0 0 1 -3.5 -3.5a4.75 4.75 0 0 1 -3.5 3.5a4.75 4.75 0 0 1 3.5 3.5' />
                                                    </svg>
                                                    {autopopulatedColumnCount} Columns
                                                </Button>
                                            </AutopopulatedColumnsDropdown>
                                        </Col>
                                    </Row>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </Modal>
            {dirtyDraftModal}
        </>
    );
};

export default CreateListModal;
